import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Circle,
  Clock,
  Calendar,
  User,
  Edit2,
  Trash2,
  AlertCircle,
  ArrowRight,
  Filter,
  ArrowUp,
  ArrowDown,
  XCircle,
  ChevronUp,
  ChevronDown,
  Timer,
  Hourglass,
} from "lucide-react";
import TimeProgressBar from "./TimeProgressBar";
import TimeDetailsPanel from "./TimeDetailsPanel";
import TimeStatsSummary from "./TimeStatsSummary";
import { useTaskTimeEntries } from "../hooks/useTaskTimeEntries";

/**
 * TaskList component for displaying and managing project tasks
 *
 * @param {Array} tasks - List of tasks to display
 * @param {boolean} isLoading - Loading state indicator
 * @param {Function} onUpdateTaskStatus - Callback for updating task status
 * @param {Function} onEditTask - Callback for editing a task
 * @param {Function} onDeleteTask - Callback for deleting a task
 * @param {boolean} showFilters - Whether to show filtering options
 */
const TaskList = ({
  tasks = [],
  isLoading = false,
  onUpdateTaskStatus,
  onEditTask,
  onDeleteTask,
  showFilters = true,
}) => {
  // Filter state
  const [filters, setFilters] = useState({
    status: "all",
    priority: "all",
    assignedTo: "",
  });

  // Sorting state
  const [sortBy, setSortBy] = useState("due_date");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Filter panel expansion state (start minimized)
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  
  // Track which tasks have expanded time details
  const [expandedTimeTasks, setExpandedTimeTasks] = useState({});
  
  // Store time data for each task
  const [taskTimeData, setTaskTimeData] = useState({});

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
  };
  
  // Toggle time details expansion for a specific task
  const toggleTimeDetails = (taskId) => {
    setExpandedTimeTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };
  
  // Fetch time data for a task and store it
  const fetchTaskTimeData = async (taskId) => {
    try {
      const { getTaskTimeSummary } = await import('../functions/api_fethcers');
      const timeSummary = await getTaskTimeSummary(taskId);
      
      setTaskTimeData(prev => ({
        ...prev,
        [taskId]: timeSummary
      }));
      
      return timeSummary;
    } catch (error) {
      console.error(`Error fetching time data for task ${taskId}:`, error);
      return null;
    }
  };
  
  // Load time data for all tasks when the component mounts or tasks change
  useEffect(() => {
    // Don't fetch if no tasks or already loading
    if (isLoading || tasks.length === 0) return;
    
    const fetchAllTasksTimeData = async () => {
      // Create a batch of promises to fetch time data for all tasks
      const promises = tasks.map(task => fetchTaskTimeData(task._id));
      
      // Wait for all promises to resolve
      await Promise.all(promises);
    };
    
    fetchAllTasksTimeData();
  }, [tasks, isLoading]);

  // Filter and sort tasks
  const filteredAndSortedTasks = [...tasks]
    // Apply filters
    .filter((task) => {
      return (
        (filters.status === "all" || task.status === filters.status) &&
        (filters.priority === "all" || task.priority === filters.priority) &&
        (filters.assignedTo === "" ||
          (task.assigned_to &&
            task.assigned_to
              .toLowerCase()
              .includes(filters.assignedTo.toLowerCase())))
      );
    })
    // Apply sorting
    .sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "priority":
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        case "due_date":
          // Handle null due dates (null dates come last)
          if (!a.due_date && !b.due_date) comparison = 0;
          else if (!a.due_date) comparison = 1;
          else if (!b.due_date) comparison = -1;
          else comparison = new Date(a.due_date) - new Date(b.due_date);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

  // Priority badge classes
  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      case "high":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
      case "medium":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "low":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      default:
        return "bg-grayteck-100 text-grayteck-800 dark:bg-grayteck-900/30 dark:text-grayteck-300";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "No due date";

    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const daysDiff = Math.floor((date - today) / (1000 * 60 * 60 * 24));

    if (date < today) {
      // Overdue
      return `Overdue: ${date.toLocaleDateString()}`;
    } else if (daysDiff === 0) {
      return "Today";
    } else if (daysDiff === 1) {
      return "Tomorrow";
    } else if (daysDiff < 7) {
      return `In ${daysDiff} days`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Status icon based on task status
  const getStatusIcon = (status) => {
    switch (status) {
      case "to_do":
        return <Circle className="h-5 w-5 text-grayteck-500" />;
      case "in_progress":
        return <Clock className="h-5 w-5 text-blue-500" />;
      case "completed":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "blocked":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "cancelled":
        return <XCircle className="h-5 w-5 text-grayteck-500" />;
      default:
        return <Circle className="h-5 w-5 text-grayteck-500" />;
    }
  };

  // Calculate if a task is overdue
  const isOverdue = (dueDate, status) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dueDate) < today && status !== "completed";
  };

  // Get task date display class (red for overdue)
  const getDateClass = (dueDate, status) => {
    return isOverdue(dueDate, status)
      ? "text-red-600 dark:text-red-400 font-medium"
      : "text-grayteck-600 dark:text-grayteck-400";
  };

  // Next status mapping for progress button
  const getNextStatus = (currentStatus) => {
    const statusMap = {
      to_do: "in_progress",
      in_progress: "completed",
      completed: "to_do",
      blocked: "in_progress",
      cancelled: "to_do"
    };
    return statusMap[currentStatus] || "to_do";
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-grayteck-600"></div>
      </div>
    );
  }

  // Empty state
  if (filteredAndSortedTasks.length === 0) {
    return (
      <div className="bg-white dark:bg-darkbg-800 rounded-lg p-6 shadow-md text-center">
        <XCircle className="mx-auto h-12 w-12 text-grayteck-400 mb-4" />
        <h3 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100 mb-2">
          No tasks found
        </h3>
        <p className="text-grayteck-600 dark:text-grayteck-400">
          {tasks.length > 0
            ? "No tasks match your current filters"
            : "Get started by creating your first task"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Summary Section */}
      <TimeStatsSummary tasks={filteredAndSortedTasks} timeData={taskTimeData} />
      
      {/* Filters Section */}
      {showFilters && (
        <div className="bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md mb-4 transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <Filter className="h-4 w-4 text-grayteck-600 dark:text-grayteck-400 mr-2" />
              <h3 className="text-sm font-medium text-grayteck-900 dark:text-grayteck-100">
                Filters
              </h3>
              
              {/* Filter status indicators - show when minimized */}
              {!isFilterExpanded && (
                <div className="ml-3 flex flex-wrap items-center gap-2">
                  {filters.status !== "all" && (
                    <span className="px-2 py-0.5 text-xs bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300 rounded-full">
                      Status: {filters.status.replace('_', ' ')}
                    </span>
                  )}
                  {filters.priority !== "all" && (
                    <span className="px-2 py-0.5 text-xs bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300 rounded-full">
                      Priority: {filters.priority}
                    </span>
                  )}
                  {filters.assignedTo && (
                    <span className="px-2 py-0.5 text-xs bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300 rounded-full">
                      Assigned: {filters.assignedTo}
                    </span>
                  )}
                  {sortBy && (
                    <span className="px-2 py-0.5 text-xs bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300 rounded-full">
                      Sort: {sortBy.replace('_', ' ')} {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Expand/Collapse button */}
            <button 
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className="p-1 rounded hover:bg-grayteck-100 dark:hover:bg-darkbg-700 text-grayteck-600 dark:text-grayteck-400"
              title={isFilterExpanded ? "Minimize filters" : "Expand filters"}
            >
              {isFilterExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Filter controls - only show when expanded */}
          {isFilterExpanded && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label
                    htmlFor="status-filter"
                    className="block text-xs text-grayteck-600 dark:text-grayteck-400 mb-1"
                  >
                    Status
                  </label>
                  <select
                    id="status-filter"
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    className="w-full p-2 text-sm border border-grayteck-300 dark:border-grayteck-700 rounded focus:ring-grayteck-500 focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                  >
                    <option value="all">All Statuses</option>
                    <option value="to_do">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="blocked">Blocked</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Priority Filter */}
                <div>
                  <label
                    htmlFor="priority-filter"
                    className="block text-xs text-grayteck-600 dark:text-grayteck-400 mb-1"
                  >
                    Priority
                  </label>
                  <select
                    id="priority-filter"
                    value={filters.priority}
                    onChange={(e) => handleFilterChange("priority", e.target.value)}
                    className="w-full p-2 text-sm border border-grayteck-300 dark:border-grayteck-700 rounded focus:ring-grayteck-500 focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                  >
                    <option value="all">All Priorities</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                {/* Assigned To Filter */}
                <div>
                  <label
                    htmlFor="assigned-filter"
                    className="block text-xs text-grayteck-600 dark:text-grayteck-400 mb-1"
                  >
                    Assigned To
                  </label>
                  <input
                    id="assigned-filter"
                    type="text"
                    value={filters.assignedTo}
                    onChange={(e) =>
                      handleFilterChange("assignedTo", e.target.value)
                    }
                    placeholder="Filter by assignee"
                    className="w-full p-2 text-sm border border-grayteck-300 dark:border-grayteck-700 rounded focus:ring-grayteck-500 focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                  />
                </div>
              </div>

              {/* Sort Controls */}
              <div className="mt-3 pt-3 border-t border-grayteck-200 dark:border-grayteck-700">
                <div className="flex items-center mb-2">
                  <span className="text-xs text-grayteck-600 dark:text-grayteck-400 mr-2">
                    Sort by:
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleSortChange("name")}
                      className={`text-xs py-1 px-2 rounded ${
                        sortBy === "name"
                          ? "bg-grayteck-600 text-white"
                          : "bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300"
                      }`}
                    >
                      Name
                    </button>
                    <button
                      onClick={() => handleSortChange("priority")}
                      className={`text-xs py-1 px-2 rounded ${
                        sortBy === "priority"
                          ? "bg-grayteck-600 text-white"
                          : "bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300"
                      }`}
                    >
                      Priority
                    </button>
                    <button
                      onClick={() => handleSortChange("due_date")}
                      className={`text-xs py-1 px-2 rounded ${
                        sortBy === "due_date"
                          ? "bg-grayteck-600 text-white"
                          : "bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300"
                      }`}
                    >
                      Due Date
                    </button>
                  </div>
                  <button
                    onClick={() =>
                      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
                    }
                    className="ml-2 p-1 rounded bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-300"
                  >
                    {sortDirection === "asc" ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tasks List */}
      <ul className="space-y-3">
        {filteredAndSortedTasks.map((task) => {
          // Get time data for this task if available
          const timeData = taskTimeData[task._id];
          const hasTimeData = !!timeData;
          const isTimeEntryActive = hasTimeData && timeData.activeEntries > 0;
          const actualHours = hasTimeData ? timeData.totalHours : 0;
          const estimatedHours = task.estimated_hours ? parseFloat(task.estimated_hours) : 0;
          
          return (
            <motion.li
              key={task._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                {/* Task Status Icon (clickable only if not completed or cancelled) */}
                {task.status !== 'completed' && task.status !== 'cancelled' ? (
                  <button
                    onClick={() =>
                      onUpdateTaskStatus({
                        taskId: task._id,
                        status: getNextStatus(task.status),
                      })
                    }
                    className="flex-shrink-0 mr-2 hover:scale-110 transition-transform cursor-pointer"
                    title={`Mark as ${getNextStatus(task.status).replace(
                      "_",
                      " "
                    )}`}
                  >
                    {getStatusIcon(task.status)}
                  </button>
                ) : (
                  <span className="flex-shrink-0 mr-2">
                    {getStatusIcon(task.status)}
                  </span>
                )}

                {/* Task Content */}
                <div className="flex-grow">
                  <div className="flex justify-between items-start">
                    <h3 className="text-md font-medium text-grayteck-900 dark:text-grayteck-100">
                      {task.name}
                    </h3>
                    
                    {/* Time indicator */}
                    {hasTimeData && (
                      <div className="ml-2 flex items-center">
                        {isTimeEntryActive && (
                          <Clock className="h-3 w-3 text-green-500 dark:text-green-400 animate-pulse mr-1" />
                        )}
                        <span className={`text-xs font-medium ${
                          isTimeEntryActive 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-grayteck-600 dark:text-grayteck-400'
                        }`}>
                          {actualHours.toFixed(1)}h
                          {estimatedHours > 0 && ` / ${estimatedHours}h`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Active Time Entry Description */}
                  {isTimeEntryActive && timeData.timeEntries && (
                    <div className="mt-1">
                      {timeData.timeEntries.filter(entry => entry.status === 'running' || entry.status === 'paused').map((entry, idx) => (
                        <div key={idx} className="flex items-start">
                          <span className="text-xs italic text-grayteck-500 dark:text-grayteck-400 mr-1">
                            ⏱️ Currently tracking:
                          </span>
                          <span className="text-xs text-grayteck-600 dark:text-grayteck-300 font-medium">
                            {entry.description || 'No description'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Task Description */}
                  {task.description && (
                    <p className="text-sm text-grayteck-600 dark:text-grayteck-400 mt-1">
                      {task.description}
                    </p>
                  )}
                  
                  {/* Time Progress Bar */}
                  {(hasTimeData || estimatedHours > 0) && (
                    <div className="mt-2">
                      <TimeProgressBar 
                        actualHours={actualHours}
                        estimatedHours={estimatedHours}
                        showLabel={false}
                        isActive={isTimeEntryActive}
                        size="sm"
                      />
                    </div>
                  )}

                  <div className="flex items-center flex-wrap gap-2 mt-2">
                    {/* Priority Badge */}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeClass(
                        task.priority
                      )}`}
                    >
                      {task.priority.charAt(0).toUpperCase() +
                        task.priority.slice(1)}
                    </span>

                    {/* Due Date */}
                    {task.due_date && (
                      <span
                        className={`flex items-center text-xs ${getDateClass(
                          task.due_date,
                          task.status
                        )}`}
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(task.due_date)}
                      </span>
                    )}

                    {/* Assigned To */}
                    {task.assigned_to && (
                      <span className="flex items-center text-xs text-grayteck-600 dark:text-grayteck-400">
                        <User className="h-3 w-3 mr-1" />
                        {task.assigned_to}
                      </span>
                    )}

                    {/* Estimated Hours */}
                    {task.estimated_hours && (
                      <span className="flex items-center text-xs text-grayteck-600 dark:text-grayteck-400">
                        <Hourglass className="h-3 w-3 mr-1" />
                        Est: {task.estimated_hours}h
                      </span>
                    )}
                    
                    {/* Time entries badge - clickable to toggle details */}
                    {hasTimeData && timeData.totalEntries > 0 && (
                      <button
                        onClick={() => toggleTimeDetails(task._id)}
                        className="flex items-center text-xs px-2 py-0.5 rounded-full bg-grayteck-100 dark:bg-grayteck-800 text-grayteck-700 dark:text-grayteck-300 hover:bg-grayteck-200 dark:hover:bg-grayteck-700"
                      >
                        <Timer className="h-3 w-3 mr-1" />
                        {timeData.totalEntries} time {timeData.totalEntries === 1 ? 'entry' : 'entries'}
                        {expandedTimeTasks[task._id] ? (
                          <ChevronUp className="h-3 w-3 ml-1" />
                        ) : (
                          <ChevronDown className="h-3 w-3 ml-1" />
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Time Details Panel - expandable */}
                  {hasTimeData && expandedTimeTasks[task._id] && (
                    <TimeDetailsPanel 
                      timeEntries={timeData.timeEntries} 
                      taskId={task._id}
                    />
                  )}
                </div>

                {/* Task Actions */}
                <div className="flex items-center space-x-1 ml-4">
                  {/* Edit Button */}
                  <button
                    onClick={() => {
                      console.log("Edit task:", task);
                      onEditTask(task);
                    }}
                    className="p-2 text-grayteck-600 hover:text-grayteck-900 dark:text-grayteck-400 dark:hover:text-grayteck-200 transition-colors rounded-md hover:bg-grayteck-100 dark:hover:bg-darkbg-700"
                    title="Edit Task"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  {/* Show different buttons based on task status */}
                  {task.status !== 'completed' && task.status !== 'cancelled' ? (
                    /* Move to Next Status button for active tasks */
                    <button
                      onClick={() =>
                        onUpdateTaskStatus({
                          taskId: task._id,
                          status: getNextStatus(task.status),
                        })
                      }
                      className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30"
                      title={`Move to ${getNextStatus(task.status).replace(
                        "_",
                        " "
                      )}`}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    /* Reactivate button for completed or cancelled tasks */
                    <button
                      onClick={() =>
                        onUpdateTaskStatus({
                          taskId: task._id,
                          status: 'to_do',
                        })
                      }
                      className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors rounded-md hover:bg-green-100 dark:hover:bg-green-900/30"
                      title="Reactivate task"
                    >
                      <ArrowRight className="w-4 h-4 transform rotate-[-135deg]" />
                    </button>
                  )}

                  {/* Delete Button */}
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this task?"
                        )
                      ) {
                        onDeleteTask(task._id);
                      }
                    }}
                    className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors rounded-md hover:bg-red-100 dark:hover:bg-red-900/30"
                    title="Delete Task"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
};

export default TaskList;
